import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  FilePdfOutlined,
  PlusOutlined,
  RollbackOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  hrDepthApi,
  type PayrollItem,
  type PayrollRun,
} from '@/api/hr-depth';
import { financeApi } from '@/api/finance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { downloadFile } from '@/shared/lib/download';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';

const PKR = (n: number | string | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function PayrollPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canApprove = useAuthStore((s) => s.hasAuthority('HR_MGMT:APPROVE'));
  const askReason = useReasonPrompt();

  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm<{ academicYearId: number; payrollMonth: Dayjs }>();
  const [openRunId, setOpenRunId] = useState<number | null>(null);

  const { data: years } = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: () => setupApi.listAcademicYears(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'payroll', 'runs'],
    queryFn: () => hrDepthApi.listRuns(),
  });

  const create = useMutation({
    mutationFn: (v: { academicYearId: number; payrollMonth: Dayjs }) =>
      hrDepthApi.createRun({
        academicYearId: v.academicYearId,
        payrollMonth: v.payrollMonth.startOf('month').format('YYYY-MM-DD'),
      }),
    onSuccess: (r) => {
      message.success(`Draft for ${dayjs(r.payrollMonth).format('MMM YYYY')} created · ${r.itemCount} items`);
      qc.invalidateQueries({ queryKey: ['hr', 'payroll', 'runs'] });
      setCreating(false);
      createForm.resetFields();
      setOpenRunId(r.id);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const [finalizeTarget, setFinalizeTarget] = useState<PayrollRun | null>(null);

  const finalize = useMutation({
    mutationFn: ({ id, bankAccountId }: { id: number; bankAccountId: number }) =>
      hrDepthApi.finalizeRun(id, { bankAccountId }),
    onSuccess: (r) => {
      message.success(`${dayjs(r.payrollMonth).format('MMM YYYY')} finalized · slips assigned`);
      qc.invalidateQueries({ queryKey: ['hr', 'payroll'] });
      setFinalizeTarget(null);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => hrDepthApi.reverseRun(id, reason),
    onSuccess: () => {
      message.success('Run reversed');
      qc.invalidateQueries({ queryKey: ['hr', 'payroll'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<PayrollRun>['columns'] = [
    {
      title: 'Month',
      dataIndex: 'payrollMonth',
      width: 130,
      render: (d: string) => dayjs(d).format('MMMM YYYY'),
    },
    { title: 'Year', dataIndex: 'academicYearName', width: 130 },
    {
      title: 'Status',
      width: 130,
      render: (_, row) =>
        row.reversed ? (
          <Tag color="default">Reversed</Tag>
        ) : row.status === 'FINALIZED' ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>Finalized</Tag>
        ) : (
          <Tag color="processing">Draft</Tag>
        ),
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      width: 80,
      align: 'right',
    },
    {
      title: 'Gross',
      dataIndex: 'totalGross',
      width: 140,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Net',
      dataIndex: 'totalNet',
      width: 150,
      align: 'right',
      render: (v: string) => <Typography.Text strong>PKR {PKR(v)}</Typography.Text>,
    },
    {
      title: 'Actions',
      width: 220,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="text" onClick={() => setOpenRunId(row.id)}>
            Open
          </Button>
          {canApprove && row.status === 'DRAFT' && !row.reversed && (
            <Button
              size="small"
              type="text"
              icon={<CheckCircleOutlined />}
              onClick={() => setFinalizeTarget(row)}
            >
              Finalize
            </Button>
          )}
          {canApprove && row.status === 'FINALIZED' && !row.reversed && (
            <Button
              size="small"
              type="text"
              danger
              icon={<RollbackOutlined />}
              onClick={async () => {
                const reason = await askReason({
                  title: 'Reverse payroll run?',
                  placeholder: 'Why is this payroll run being reversed?',
                  okText: 'Reverse run',
                  danger: true,
                });
                if (reason) reverse.mutate({ id: row.id, reason });
              }}
            >
              Reverse
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Payroll"
        description="Monthly payroll runs. Draft → finalize lifecycle. Items are auto-built from each staff member's active salary structure."
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
              createForm.resetFields();
              const currentYear = years?.find((y) => y.current);
              createForm.setFieldsValue({
                payrollMonth: dayjs().startOf('month'),
                academicYearId: currentYear?.id,
              });
              setCreating(true);
            }}>
              New run
            </Button>
          ) : null
        }
      />

      {data && data.length === 0 && !isLoading ? (
        <Empty description="No payroll runs yet" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />
        </Card>
      )}

      <Modal
        title="Create payroll run"
        open={creating}
        onCancel={() => setCreating(false)}
        onOk={() => createForm.submit()}
        confirmLoading={create.isPending}
        okText="Create draft"
        destroyOnHidden
      >
        {creating && (
          <Form form={createForm} layout="vertical" onFinish={(v) => create.mutate(v)}>
            <Form.Item label="Academic year" name="academicYearId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(years ?? []).map((y) => ({
                  value: y.id,
                  label: `${y.name}${y.current ? ' (current)' : ''}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Payroll month"
              name="payrollMonth"
              rules={[{ required: true }]}
              tooltip="Always saved as the first day of the month."
            >
              <DatePicker.MonthPicker style={{ width: '100%' }} format="MMMM YYYY" autoFocus />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {openRunId != null && <PayrollRunDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />}

      {finalizeTarget && (
        <FinalizeModal
          run={finalizeTarget}
          onClose={() => setFinalizeTarget(null)}
          submit={(bankAccountId) =>
            finalize.mutate({ id: finalizeTarget.id, bankAccountId })
          }
          submitting={finalize.isPending}
        />
      )}
    </div>
  );
}

function FinalizeModal({
  run,
  onClose,
  submit,
  submitting,
}: {
  run: PayrollRun;
  onClose: () => void;
  submit: (bankAccountId: number) => void;
  submitting: boolean;
}) {
  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);
  const [bankAccountId, setBankAccountId] = useState<number | undefined>(() => {
    const v = Number(localStorage.getItem('school.payroll.lastBankAccountId'));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  });

  return (
    <Modal
      open
      title={`Finalize ${dayjs(run.payrollMonth).format('MMM YYYY')}`}
      onCancel={onClose}
      okText="Finalize & post to GL"
      okButtonProps={{ disabled: bankAccountId == null, loading: submitting }}
      onOk={() => {
        if (bankAccountId == null) return;
        localStorage.setItem('school.payroll.lastBankAccountId', String(bankAccountId));
        submit(bankAccountId);
      }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        Assigns slip numbers, recovers outstanding advance EMIs, and posts a journal entry: <b>DR Salary
        Expense / CR Cash or Bank</b>. Items become read-only after finalization.
      </Typography.Paragraph>
      <Typography.Text strong>Pay from</Typography.Text>
      <Select
        style={{ width: '100%', marginTop: 6 }}
        placeholder="Select cash drawer or bank"
        loading={banksQuery.isLoading}
        value={bankAccountId}
        onChange={setBankAccountId}
        options={banks.map((b) => ({
          value: b.id,
          label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}${b.cash ? ' (cash)' : ''}`,
        }))}
      />
    </Modal>
  );
}

function PayrollRunDrawer({ runId, onClose }: { runId: number; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [editingItem, setEditingItem] = useState<PayrollItem | null>(null);
  const [staffFilter, setStaffFilter] = useState('');
  const [itemForm] = Form.useForm<{
    absentDeduction: number;
    unpaidLeaveDeduction: number;
    manualAdditions: number;
    manualDeductions: number;
    advanceDeduction: number;
    remarks?: string;
  }>();

  const { data: run } = useQuery({
    queryKey: ['hr', 'payroll', 'runs', runId],
    queryFn: () => hrDepthApi.getRun(runId),
  });

  const { data: items, isFetching } = useQuery({
    queryKey: ['hr', 'payroll', 'runs', runId, 'items'],
    queryFn: () => hrDepthApi.runItems(runId),
  });

  const filteredItems = useMemo(() => {
    const q = staffFilter.trim().toLowerCase();
    if (!q) return items ?? [];
    return (items ?? []).filter((i) =>
      (i.staffName ?? '').toLowerCase().includes(q) ||
      String(i.salarySlipNumber ?? '').includes(q),
    );
  }, [items, staffFilter]);

  const totals = useMemo(() => {
    const rows = filteredItems;
    const sum = (k: keyof PayrollItem) =>
      rows.reduce((s, r) => s + Number(r[k] ?? 0), 0);
    return {
      count: rows.length,
      gross: sum('grossSalary'),
      net: sum('netSalary'),
      deductions:
        sum('absentDeduction') +
        sum('unpaidLeaveDeduction') +
        sum('manualDeductions') +
        sum('advanceDeduction'),
      additions: sum('manualAdditions'),
    };
  }, [filteredItems]);

  const update = useMutation({
    mutationFn: (v: {
      absentDeduction: number;
      unpaidLeaveDeduction: number;
      manualAdditions: number;
      manualDeductions: number;
      advanceDeduction: number;
      remarks?: string;
    }) =>
      hrDepthApi.updateItem(editingItem!.id, {
        absentDeduction: Number(v.absentDeduction).toFixed(2),
        unpaidLeaveDeduction: Number(v.unpaidLeaveDeduction).toFixed(2),
        manualAdditions: Number(v.manualAdditions).toFixed(2),
        manualDeductions: Number(v.manualDeductions).toFixed(2),
        advanceDeduction: Number(v.advanceDeduction).toFixed(2),
        remarks: v.remarks?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Item updated');
      qc.invalidateQueries({ queryKey: ['hr', 'payroll'] });
      setEditingItem(null);
      itemForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openEdit = (item: PayrollItem) => {
    setEditingItem(item);
    itemForm.setFieldsValue({
      absentDeduction: Number(item.absentDeduction),
      unpaidLeaveDeduction: Number(item.unpaidLeaveDeduction),
      manualAdditions: Number(item.manualAdditions),
      manualDeductions: Number(item.manualDeductions),
      advanceDeduction: Number(item.advanceDeduction),
      remarks: item.remarks ?? undefined,
    });
  };

  const isDraft = run?.status === 'DRAFT' && !run.reversed;

  return (
    <Drawer
      open
      onClose={onClose}
      size={900}
      title={run ? `Payroll · ${dayjs(run.payrollMonth).format('MMMM YYYY')}` : 'Payroll'}
    >
      {run && (
        <Space wrap size={[12, 4]} style={{ marginBottom: 12 }}>
          <Tag color={run.reversed ? 'default' : run.status === 'FINALIZED' ? 'success' : 'processing'}>
            {run.reversed ? 'Reversed' : run.status}
          </Tag>
          <Tag>{run.academicYearName}</Tag>
          <Tag color="processing" style={{ fontSize: 13 }}>
            Net · PKR {PKR(run.totalNet)}
          </Tag>
        </Space>
      )}

      <Space style={{ marginBottom: 12, width: '100%' }} size={12} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Filter by staff name or slip #"
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          style={{ width: 280 }}
        />
        <Statistic
          title="Shown"
          value={totals.count}
          suffix={items && totals.count !== items.length ? `/ ${items.length}` : undefined}
          styles={{ content: { fontSize: 16 } }}
        />
        <Statistic
          title="Gross"
          value={PKR(totals.gross)}
          prefix="PKR"
          styles={{ content: { fontSize: 16 } }}
        />
        <Statistic
          title="Net"
          value={PKR(totals.net)}
          prefix="PKR"
          styles={{ content: { fontSize: 16 } }}
        />
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={isFetching}
        dataSource={filteredItems}
        pagination={{ pageSize: 50 }}
        onRow={(row) => ({
          onClick: () => {
            if (isDraft && canEdit) openEdit(row);
          },
          style: { cursor: isDraft && canEdit ? 'pointer' : 'default' },
        })}
        columns={[
          {
            title: 'Slip',
            dataIndex: 'salarySlipNumber',
            width: 100,
            render: (n: number | null) =>
              n ? <Tag color="processing" style={{ fontFamily: 'monospace' }}>#{n}</Tag> : <span style={{ opacity: 0.4 }}>—</span>,
          },
          { title: 'Staff', dataIndex: 'staffName' },
          {
            title: 'Gross',
            dataIndex: 'grossSalary',
            width: 120,
            align: 'right',
            render: (v: string) => `PKR ${PKR(v)}`,
          },
          {
            title: 'Deductions',
            width: 250,
            align: 'right',
            render: (_, row) => (
              <Space orientation="vertical" size={0} style={{ textAlign: 'right' }}>
                {Number(row.absentDeduction) > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Absent: PKR {PKR(row.absentDeduction)}
                  </Typography.Text>
                )}
                {Number(row.unpaidLeaveDeduction) > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Unpaid leave: PKR {PKR(row.unpaidLeaveDeduction)}
                  </Typography.Text>
                )}
                {Number(row.manualDeductions) > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Manual: PKR {PKR(row.manualDeductions)}
                  </Typography.Text>
                )}
                {Number(row.advanceDeduction) > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Advance: PKR {PKR(row.advanceDeduction)}
                  </Typography.Text>
                )}
                {Number(row.manualAdditions) > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    + Bonus / arrears: PKR {PKR(row.manualAdditions)}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Net',
            dataIndex: 'netSalary',
            width: 130,
            align: 'right',
            render: (v: string) => <Typography.Text strong>PKR {PKR(v)}</Typography.Text>,
          },
          {
            title: '',
            width: 130,
            align: 'right',
            render: (_, row) => (
              <Space size={0}>
                {!isDraft && row.salarySlipNumber && (
                  <Button
                    size="small"
                    type="link"
                    icon={<FilePdfOutlined />}
                    onClick={() =>
                      downloadFile(`/reports/exports/salary-slip/${row.id}.pdf`, {},
                        `salary-slip-${row.salarySlipNumber}.pdf`)
                    }
                  >
                    Slip
                  </Button>
                )}
                {isDraft && canEdit && (
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editingItem ? `Adjust · ${editingItem.staffName}` : 'Adjust item'}
        open={editingItem != null}
        onCancel={() => setEditingItem(null)}
        onOk={() => itemForm.submit()}
        confirmLoading={update.isPending}
        okText="Save"
        destroyOnHidden
        width={520}
      >
        {editingItem && (
          <Form form={itemForm} layout="vertical" onFinish={(v) => update.mutate(v)}>
            <Typography.Paragraph type="secondary">
              Gross PKR {PKR(editingItem.grossSalary)} (from active structure). Net is auto-recomputed.
            </Typography.Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Absent deduction" name="absentDeduction" rules={[{ required: true, type: 'number', min: 0 }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
              </Form.Item>
              <Form.Item label="Unpaid leave deduction" name="unpaidLeaveDeduction" rules={[{ required: true, type: 'number', min: 0 }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Manual additions" name="manualAdditions" rules={[{ required: true, type: 'number', min: 0 }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
              </Form.Item>
              <Form.Item label="Manual deductions" name="manualDeductions" rules={[{ required: true, type: 'number', min: 0 }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Advance deduction" name="advanceDeduction" rules={[{ required: true, type: 'number', min: 0 }]}>
              <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Drawer>
  );
}
